import React from 'react';
import { getSupabaseClient } from '../supabaseClient';

type RequestRow = {
  id: string;
  book_id: string;
  book_title: string;
  book_author: string;
  owner_id: string;
  owner_email: string;
  owner_nickname: string;
  reader_id: string;
  reader_email: string;
  reader_nickname: string;
  status: string;
  address: string;
  phone: string;
  courier_cost: number | null;
  estimated_days: number | null;
  upi_id: string | null;
  tracking_number: string | null;
  created_at: string;
  updated_at: string;
};

export default function Requests() {
  const supabase = getSupabaseClient();
  const [requests, setRequests] = React.useState<RequestRow[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('all');

  const loadRequests = React.useCallback(async () => {
    setLoading(true);
    try {
      // Load requests with book and user information
      const { data: requestsData, error } = await supabase
        .from('requests')
        .select(`
          *,
          books!requests_book_id_fkey (
            title,
            author
          ),
          owner:users!requests_owner_id_fkey (
            email,
            nickname
          ),
          reader:users!requests_reader_id_fkey (
            email,
            nickname
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const requestsWithDetails = (requestsData || []).map((request: any) => ({
        ...request,
        book_title: request.books?.title || 'Unknown',
        book_author: request.books?.author || 'Unknown',
        owner_email: request.owner?.email || 'Unknown',
        owner_nickname: request.owner?.nickname || 'Unknown',
        reader_email: request.reader?.email || 'Unknown',
        reader_nickname: request.reader?.nickname || 'Unknown',
      }));

      setRequests(requestsWithDetails);
    } catch (error) {
      console.error('Failed to load requests:', error);
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  React.useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const filteredRequests = requests.filter(request => {
    const matchesSearch = 
      request.book_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.book_author.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.owner_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.reader_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (request.tracking_number && request.tracking_number.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus = statusFilter === 'all' || request.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const updateRequestStatus = async (requestId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('requests')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', requestId);

      if (error) throw error;

      // Update local state
      setRequests(prev => prev.map(request =>
        request.id === requestId ? { ...request, status: newStatus } : request
      ));

      alert('Request status updated successfully');
    } catch (error) {
      console.error('Failed to update request status:', error);
      alert('Failed to update request status');
    }
  };

  const updateTrackingNumber = async (requestId: string, trackingNumber: string) => {
    try {
      const { error } = await supabase
        .from('requests')
        .update({ tracking_number: trackingNumber, updated_at: new Date().toISOString() })
        .eq('id', requestId);

      if (error) throw error;

      // Update local state
      setRequests(prev => prev.map(request =>
        request.id === requestId ? { ...request, tracking_number: trackingNumber } : request
      ));

      alert('Tracking number updated successfully');
    } catch (error) {
      console.error('Failed to update tracking number:', error);
      alert('Failed to update tracking number');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending_owner_reply': return '#ffc107';
      case 'info_gathering': return '#17a2b8';
      case 'replied': return '#28a745';
      case 'rejected': return '#dc3545';
      case 'payment_pending': return '#fd7e14';
      case 'paid': return '#20c997';
      case 'shipped': return '#007bff';
      case 'received': return '#6610f2';
      case 'being_read': return '#6f42c1';
      case 'returned': return '#e83e8c';
      case 'closed': return '#6c757d';
      default: return '#6c757d';
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      'pending_owner_reply': 'Pending Reply',
      'info_gathering': 'Info Gathering',
      'replied': 'Replied',
      'rejected': 'Rejected',
      'payment_pending': 'Payment Pending',
      'paid': 'Paid',
      'shipped': 'Shipped',
      'received': 'Received',
      'being_read': 'Being Read',
      'returned': 'Returned',
      'closed': 'Closed',
    };
    return labels[status] || status;
  };

  return (
    <div>
      <h1 className="page-header">
        Request Management
      </h1>
      <div className="page-subtitle">
        Manage book requests and their status
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ margin: 0 }}>All Requests</h2>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Search requests..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                minWidth: '200px'
              }}
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: '4px'
              }}
            >
              <option value="all">All Status</option>
              <option value="pending_owner_reply">Pending Reply</option>
              <option value="info_gathering">Info Gathering</option>
              <option value="replied">Replied</option>
              <option value="rejected">Rejected</option>
              <option value="payment_pending">Payment Pending</option>
              <option value="paid">Paid</option>
              <option value="shipped">Shipped</option>
              <option value="received">Received</option>
              <option value="being_read">Being Read</option>
              <option value="returned">Returned</option>
              <option value="closed">Closed</option>
            </select>
            <button onClick={loadRequests}>Refresh</button>
          </div>
        </div>

        {loading ? (
          <div>Loading requests...</div>
        ) : (
          <>
            <div style={{ marginBottom: '16px' }}>
              <strong>{filteredRequests.length}</strong> request(s) found
              {searchTerm && (
                <span className="muted" style={{ marginLeft: '8px' }}>
                  (filtered from {requests.length} total requests)
                </span>
              )}
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Book</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Owner</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Reader</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Status</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Courier Cost</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Tracking</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Created</th>
                    <th style={{ textAlign: 'left', padding: '8px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRequests.map((request) => (
                    <tr key={request.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '8px' }}>
                        <div>
                          <div style={{ fontWeight: '500' }}>{request.book_title}</div>
                          <div style={{ fontSize: '12px', color: '#666' }}>by {request.book_author}</div>
                        </div>
                      </td>
                      <td style={{ padding: '8px' }}>
                        <div>
                          <div style={{ fontWeight: '500' }}>{request.owner_nickname}</div>
                          <div style={{ fontSize: '12px', color: '#666' }}>{request.owner_email}</div>
                        </div>
                      </td>
                      <td style={{ padding: '8px' }}>
                        <div>
                          <div style={{ fontWeight: '500' }}>{request.reader_nickname}</div>
                          <div style={{ fontSize: '12px', color: '#666' }}>{request.reader_email}</div>
                        </div>
                      </td>
                      <td style={{ padding: '8px' }}>
                        <select
                          value={request.status}
                          onChange={(e) => updateRequestStatus(request.id, e.target.value)}
                          style={{
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            background: getStatusColor(request.status),
                            color: '#fff',
                            border: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="pending_owner_reply">Pending Reply</option>
                          <option value="info_gathering">Info Gathering</option>
                          <option value="replied">Replied</option>
                          <option value="rejected">Rejected</option>
                          <option value="payment_pending">Payment Pending</option>
                          <option value="paid">Paid</option>
                          <option value="shipped">Shipped</option>
                          <option value="received">Received</option>
                          <option value="being_read">Being Read</option>
                          <option value="returned">Returned</option>
                          <option value="closed">Closed</option>
                        </select>
                      </td>
                      <td style={{ padding: '8px' }}>
                        {request.courier_cost ? `₹${request.courier_cost}` : '—'}
                      </td>
                      <td style={{ padding: '8px' }}>
                        {request.tracking_number ? (
                          <div>
                            <div>{request.tracking_number}</div>
                            <input
                              type="text"
                              placeholder="Update tracking..."
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  updateTrackingNumber(request.id, (e.target as HTMLInputElement).value);
                                }
                              }}
                              style={{
                                fontSize: '12px',
                                padding: '2px 4px',
                                width: '120px',
                                marginTop: '2px'
                              }}
                            />
                          </div>
                        ) : (
                          <input
                            type="text"
                            placeholder="Add tracking number..."
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                updateTrackingNumber(request.id, (e.target as HTMLInputElement).value);
                              }
                            }}
                            style={{
                              fontSize: '12px',
                              padding: '2px 4px',
                              width: '120px'
                            }}
                          />
                        )}
                      </td>
                      <td style={{ padding: '8px' }}>{new Date(request.created_at).toLocaleDateString()}</td>
                      <td style={{ padding: '8px' }}>
                        <button
                          onClick={() => {
                            const notes = prompt('Add admin notes:');
                            if (notes) {
                              // In a real implementation, you would save these notes
                              alert('Notes would be saved here in a real implementation');
                            }
                          }}
                          style={{
                            fontSize: '12px',
                            padding: '2px 6px',
                            background: '#6c757d',
                            border: 'none',
                            color: 'white',
                            borderRadius: '3px',
                            cursor: 'pointer'
                          }}
                        >
                          Add Notes
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <div className="card">
        <h2>Request Statistics</h2>
        <div className="stats-grid">
          <div className="metric-card">
            <div className="metric-value stats-card-value-primary">{requests.length}</div>
            <div className="metric-label">Total Requests</div>
          </div>
          <div className="metric-card">
            <div className="metric-value stats-card-value-success">
              {requests.filter(r => !['closed', 'returned', 'rejected'].includes(r.status)).length}
            </div>
            <div className="metric-label">Active</div>
          </div>
          <div className="metric-card">
            <div className="metric-value stats-card-value-warning">
              {requests.filter(r => ['closed', 'returned'].includes(r.status)).length}
            </div>
            <div className="metric-label">Completed</div>
          </div>
          <div className="metric-card">
            <div className="metric-value stats-card-value-info">
              {requests.filter(r => r.status === 'rejected').length}
            </div>
            <div className="metric-label">Rejected</div>
          </div>
        </div>
      </div>

      <div className="card">
        <h2>Status Distribution</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {Object.entries(
            requests.reduce((acc, request) => {
              acc[request.status] = (acc[request.status] || 0) + 1;
              return acc;
            }, {} as Record<string, number>)
          ).map(([status, count]) => (
            <div key={status} className="list-item">
              <span style={{
                padding: '4px 8px',
                background: getStatusColor(status),
                color: 'white',
                borderRadius: '4px',
                fontSize: '12px',
                minWidth: '120px'
              }}>
                {getStatusLabel(status)}
              </span>
              <span style={{ fontWeight: 'bold' }}>{count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}